import { useCallback, useEffect, useMemo, useState } from 'react'
import classNames from 'classnames'
import { toast } from 'react-toastify'
import s from './packages-modal.module.scss'
import { Modal } from '@shared/ui/modal/modal'
import { Dropdown } from '@shared/ui/dropdown/dropdown'
import { Button } from '@shared/ui/button/button'
import { useProfile } from '@shared/api/useProfile'
import { usePackagesModal } from '../../model/use-packages-modal'
import { useProfilePackages } from '../../api/useProfilePackages'
import { useCreateProfilePackage } from '../../api/useCreateProfilePackage'
import { useUpdateProfilePackage } from '../../api/useUpdateProfilePackage'
import { useDeleteProfilePackage } from '../../api/useDeleteProfilePackage'
import { MAX_PACKAGES } from '../../config'
import { IconChevronRight, IconPlus } from '@tabler/icons-react'

const LESSON_COUNT_OPTIONS = [5, 10, 20]
const LESSON_COUNT_TO_DURATION_DAYS = { 5: 60, 10: 90, 20: 180 }
const STUDENT_DISCOUNT = 0.10

const getPackageImage = (lessonsCount) => {
	const count = LESSON_COUNT_OPTIONS.includes(lessonsCount) ? lessonsCount : LESSON_COUNT_OPTIONS[0]
	return `${process.env.REACT_APP_STORAGE_URL}images/history/${count}_blocks.webp`
}

const formatNumber = (value) =>
	(typeof value === 'number' && !Number.isNaN(value) ? value.toLocaleString('ru-RU') : '—')

const getDurationDays = (lessonsCount) => LESSON_COUNT_TO_DURATION_DAYS[lessonsCount] ?? null

const mapServerPackage = (pack) => ({
	id: pack?.id,
	lessonsCount: pack?.lessons_count ?? null,
	price: pack?.price ?? null,
})

const createEmptyForm = () => ({ lessonsCount: null, price: null })

export const PackagesModal = () => {
	
	const { isOpen, close, modalParams } = usePackagesModal()
	const { data: profile } = useProfile()
	const { data: rawPackages } = useProfilePackages()

	const singleLessonPrice = profile?.grade?.price ?? profile?.ts?.grade?.price ?? null

	const existingPackages = useMemo(() => {
		const list = Array.isArray(rawPackages) ? rawPackages : rawPackages?.data ?? []
		return list.map(mapServerPackage)
	}, [rawPackages])

	const [editMode, setEditMode] = useState(null) // 'create' | 'edit' | null
	const [selectedPackageId, setSelectedPackageId] = useState(null)
	const [packageForm, setPackageForm] = useState(null)

	const isCreating = editMode === 'create'

	const { mutate: createPackage, isLoading: isSaving } = useCreateProfilePackage()
	const { mutate: updatePackage, isLoading: isUpdating } = useUpdateProfilePackage()
	const { mutate: deletePackage, isLoading: isDeleting } = useDeleteProfilePackage()

	const getRecommendedPrice = (lessonsCount) =>
		(lessonsCount && singleLessonPrice ? Math.round(lessonsCount * singleLessonPrice * (1 - STUDENT_DISCOUNT)) : null)

	const selectPackage = (pack) => {
		setEditMode('edit')
		setSelectedPackageId(pack.id)
		setPackageForm({ ...pack })
	}

	const beginCreate = useCallback(() => {
		if (existingPackages.length >= MAX_PACKAGES) return
		setEditMode('create')
		setSelectedPackageId(null)
		setPackageForm(createEmptyForm())
	}, [existingPackages.length])

	useEffect(() => {
		if (!isOpen) {
			setEditMode(null)
			setSelectedPackageId(null)
			setPackageForm(null)
			return
		}
		if (modalParams?.mode === 'create') beginCreate()
	}, [isOpen, modalParams, beginCreate])

	useEffect(() => {
		if (!isOpen || editMode || modalParams?.mode === 'create') return
		if (existingPackages.length) selectPackage(existingPackages[0])
	}, [isOpen, editMode, modalParams, existingPackages])

	const availableLessonCounts = LESSON_COUNT_OPTIONS.filter(
		(count) => !existingPackages.some((pack) => pack.lessonsCount === count && pack.id !== selectedPackageId)
	)

	const isDuplicateLessonCount = (lessonsCount) =>
		existingPackages.some((pack) => pack.lessonsCount === lessonsCount && pack.id !== selectedPackageId)

	const updateForm = (patch) => setPackageForm((prev) => ({ ...prev, ...patch }))
	const selectLessonCount = (lessonsCount) => updateForm({ lessonsCount, price: getRecommendedPrice(lessonsCount) })

	const cancelCreate = () => {
		setEditMode(null)
		setSelectedPackageId(null)
		setPackageForm(null)
	}

	const saveNewPackage = () => {
		if (!packageForm?.lessonsCount) return
		if (isDuplicateLessonCount(packageForm.lessonsCount)) {
			toast.error('Пакет с таким количеством занятий уже существует')
			return
		}
		createPackage(
			{ lessons_count: packageForm.lessonsCount, price: packageForm.price ?? 0 },
			{ onSuccess: (created) => selectPackage(mapServerPackage(created)) }
		)
	}

	const originalPackage = useMemo(
		() => existingPackages.find((pack) => pack.id === selectedPackageId),
		[existingPackages, selectedPackageId]
	)

	const hasChanges = useMemo(() => {
		if (isCreating || !originalPackage || !packageForm) return true
		return originalPackage.lessonsCount !== packageForm.lessonsCount || originalPackage.price !== packageForm.price
	}, [isCreating, originalPackage, packageForm])

	const canSave = hasChanges && packageForm?.lessonsCount && !isDuplicateLessonCount(packageForm.lessonsCount)

	const saveEditedPackage = () => {
		if (isDuplicateLessonCount(packageForm.lessonsCount)) {
			toast.error('Пакет с таким количеством занятий уже существует')
			return
		}
		updatePackage(
			{ id: selectedPackageId, lessons_count: packageForm.lessonsCount, price: packageForm.price ?? 0 },
			{ onSuccess: () => close() }
		)
	}

	const removePackage = () => {
		deletePackage(selectedPackageId, {
			onSuccess: () => { setEditMode(null); setSelectedPackageId(null); setPackageForm(null) },
		})
	}

	const durationDays = packageForm?.lessonsCount ? getDurationDays(packageForm.lessonsCount) : null
	const pricePerLesson = packageForm?.lessonsCount && packageForm?.price
		? Math.round(packageForm.price / packageForm.lessonsCount)
		: null
	const separatePurchaseTotal = packageForm?.lessonsCount && singleLessonPrice
		? packageForm.lessonsCount * singleLessonPrice
		: null
	const studentBenefitPercent = packageForm?.price && separatePurchaseTotal
		? Math.max(0, Math.round((1 - packageForm.price / separatePurchaseTotal) * 100))
		: 0

	return (
		<Modal isOpen={isOpen} close={close} style={{ content: { padding: 0 } }}>
			<div className={s.modal}>
				<div className={s.header}>
					<div className={s.headerText}>
						<h5 className={s.title}>Пакеты занятий</h5>
						<p className={s.subtitle}>Создавайте и редактируйте пакеты занятий для учеников.</p>
					</div>
				</div>

				<div className={s.body}>
					<div className={s.packagesList}>
						<div className={s.packagesListHeader}>
							<span className={s.packagesListTitle}>Созданные</span>
							<span className={s.packagesCounter}>{existingPackages.length}/{MAX_PACKAGES}</span>
						</div>

						{existingPackages.map((pack) => (
							<button
								type='button'
								key={pack.id}
								className={classNames(s.packageCard, !isCreating && pack.id === selectedPackageId && s.packageCardSelected)}
								onClick={() => selectPackage(pack)}
							>
								<div className={s.packageCardContent}>
									<img className={s.packageCardImage} src={getPackageImage(pack.lessonsCount)} alt=''/>
									<div className={s.packageCardText}>
										<span className={s.packageCardCount}>{pack.lessonsCount} занятий</span>
									</div>
								</div>
								<IconChevronRight size={24} className={s.packageCardChevron}/>
							</button>
						))}

						<button
							type='button'
							className={classNames(s.packageCard, s.newPackageCard, isCreating && s.newPackageCardActive)}
							onClick={beginCreate}
							disabled={existingPackages.length >= MAX_PACKAGES && !isCreating}
						>
							<div className={s.packageCardContent}>
								<div className={classNames(s.newPackageIcon, isCreating && s.newPackageIconActive)}><IconPlus size={24}/></div>
								<div className={s.packageCardText}>
									<span className={classNames(s.packageCardCount, s.newPackageTitle)}>Создать новый пакет</span>
									<span className={s.packageCardHint}>Не больше {MAX_PACKAGES} пакетов</span>
								</div>
							</div>
							<IconChevronRight size={24} className={s.packageCardChevron}/>
						</button>
					</div>

					<div className={s.editPanel}>
						{packageForm ? <>
							<p className={s.editPanelLabel}>{isCreating ? 'Создание пакета' : 'Редактирование пакета'}</p>

							<div className={s.editPanelHeader}>
								<img className={s.editPanelImage} src={getPackageImage(packageForm.lessonsCount)} alt=''/>
								<span className={s.editPanelTitle}>
									{packageForm.lessonsCount ? `${packageForm.lessonsCount} занятий` : 'Выберите количество занятий'}
								</span>
							</div>

							<div className={s.fieldsRow}>
								<div className={s.field}>
									<span className={s.fieldLabel}>Количество занятий</span>
									<Dropdown
										label='-'
										options={availableLessonCounts}
										value={packageForm.lessonsCount}
										onChange={selectLessonCount}
										className={s.fieldControl}
									/>
								</div>
								<div className={s.field}>
									<span className={s.fieldLabel}>Срок действия, дней</span>
									<div className={s.durationField}>{durationDays ?? '-'}</div>
								</div>
							</div>

							<div className={s.field}>
								<div className={s.priceHeader}>
									<span className={s.fieldLabel}>Цена пакета</span>
									<span className={s.benefitBadge}>Выгода для ученика {studentBenefitPercent}%</span>
								</div>
								<div className={s.priceInputWrapper}>
									<input
										className={s.priceInput}
										inputMode='numeric'
										placeholder='-'
										value={packageForm.price ?? ''}
										onChange={(e) => updateForm({ price: Number(e.target.value.replace(/\D/g, '')) || null })}
									/>
									<span className={s.rubleSign}>₽</span>
								</div>
								<div className={s.priceHints}>
									<span>Рекомендуемая цена: {packageForm.lessonsCount ? `${formatNumber(getRecommendedPrice(packageForm.lessonsCount))} ₽` : '- ₽'}</span>
									<span>Стоимость одного занятия в пакете: {pricePerLesson ? `${formatNumber(pricePerLesson)} ₽` : '- ₽'}</span>
								</div>
							</div>

							<div className={s.comparisonBlock}>
								<div className={s.comparisonText}>
									<p className={s.comparisonTitle}>Сравнение с одиночным занятием</p>
									<p className={s.comparisonLine}>Цена одного занятия у вас: {formatNumber(singleLessonPrice)} ₽</p>
									<p className={s.comparisonLine}>Если ученик купит {packageForm.lessonsCount ?? '-'} занятий по отдельности: {separatePurchaseTotal ? formatNumber(separatePurchaseTotal) : '-'} ₽</p>
									<p className={s.comparisonLine}>В пакете из {packageForm.lessonsCount ?? '-'} занятий: <b>{packageForm.price ? formatNumber(packageForm.price) : '-'} ₽</b></p>
								</div>
								<img className={s.comparisonImage} src={process.env.REACT_APP_STORAGE_URL + 'images/mascot/man-thumbs-up.webp'} alt=''/>
							</div>

							<div className={s.actions}>
								{isCreating
									? <Button className={s.cancelButton} label='Отмена' onClick={cancelCreate}/>
									: <Button className={s.deleteButton} label='Удалить пакет' fetching={isDeleting} onClick={removePackage}/>}
								<Button
									className={s.saveButton}
									label='Сохранить'
									fetching={isCreating ? isSaving : isUpdating}
									disabled={!canSave}
									onClick={isCreating ? saveNewPackage : saveEditedPackage}
								/>
							</div>
						</> : (
							<div className={s.editPanelEmpty}>Выберите пакет слева или создайте новый</div>
						)}
					</div>
				</div>
			</div>
		</Modal>
	)
}
